import { useCallback, useEffect, useRef, useState } from 'react'
import { Functions } from 'firebase/functions'
import { api } from '../../../lib/api'
import { CLEO_CALLABLE_TIMEOUT_MS } from '../../../lib/shared/cleoSuggestionTiming'

// idle       — nothing running
// generating — a draft is being written
// error      — the last attempt failed; the advisor can try again
export type CleoComposeStatus = 'idle' | 'generating' | 'error'

interface CleoComposeResponse {
  suggestion?: string | null
  suggestionId?: string | null
}

export interface CleoComposeRequest {
  // Absent when the message opens a conversation that is only created on send
  // (the New conversation dialog). Cleo then drafts from the request alone.
  conversationId?: string | null
  // For a new conversation, the company the message is for; for an existing
  // one, the inbox scope (Cleo resolves the thread's company itself).
  companyUid?: string | null
  // What the advisor wants the message to say. The only input Cleo has when no
  // customer message is waiting, so it is required.
  instructions: string
  // Who the message goes to and on which channel, when known. Worth sending
  // for a new conversation, which has no thread to take either from.
  recipientName?: string | null
  channel?: 'email' | 'chat'
}

export interface UseChatwootCleoCompose {
  status: CleoComposeStatus
  // Resolves with the drafted message, or null when there is nothing to use: the
  // run failed, came back empty, was superseded by a newer call or a reset, or
  // one is already in flight. The caller decides where the text goes.
  compose: (request: CleoComposeRequest) => Promise<string | null>
  // Abandon a run in flight and return to idle — a conversation switch, or the
  // advisor closing the dialog. The run itself still finishes upstream, but its
  // result is discarded rather than landing in the wrong place.
  reset: () => void
}

/**
 * Drafts a message the advisor starts, from their own instruction (CL-2217).
 *
 * Separate from useChatwootCleoSuggestion on purpose: that hook is a state
 * machine around the draft for the awaited customer message (stored drafts,
 * polling, settling), and none of it applies here. A composed message answers
 * nothing, so nothing is stored or polled — the text is handed back to the
 * caller and belongs to the advisor from then on.
 */
export function useChatwootCleoCompose(
  functions: Functions
): UseChatwootCleoCompose {
  const [status, setStatus] = useState<CleoComposeStatus>('idle')
  // Bumped by every call and every reset; a result only counts if it still
  // carries the latest value, which is how a late response is told apart from
  // one the advisor is still waiting for.
  const runRef = useRef(0)
  const inFlightRef = useRef(false)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      runRef.current += 1
    }
  }, [])

  const compose = useCallback(
    async ({
      conversationId,
      companyUid,
      instructions,
      recipientName,
      channel,
    }: CleoComposeRequest): Promise<string | null> => {
      const trimmed = instructions.trim()
      // A second click while the first is running would spend a second run on
      // the same request.
      if (!trimmed || inFlightRef.current) {
        return null
      }
      inFlightRef.current = true
      runRef.current += 1
      const run = runRef.current
      setStatus('generating')
      try {
        const response = (await api(
          functions,
          'chatwoot/cleo_suggest',
          {
            ...(conversationId ? { conversationId } : {}),
            companyUid: companyUid || undefined,
            source: 'compose',
            composeInstructions: trimmed,
            ...(recipientName?.trim()
              ? { composeRecipientName: recipientName.trim() }
              : {}),
            ...(channel ? { composeChannel: channel } : {}),
          },
          { timeout: CLEO_CALLABLE_TIMEOUT_MS }
        )) as CleoComposeResponse
        if (!mountedRef.current || run !== runRef.current) {
          return null
        }
        const text = response?.suggestion?.trim() ?? ''
        if (!text) {
          setStatus('error')
          return null
        }
        setStatus('idle')
        return text
      } catch (_error) {
        if (mountedRef.current && run === runRef.current) {
          setStatus('error')
        }
        return null
      } finally {
        // Only the run that is still current owns the flag: after a reset, this
        // one is abandoned and a newer call may be holding it.
        if (run === runRef.current) {
          inFlightRef.current = false
        }
      }
    },
    [functions]
  )

  const reset = useCallback(() => {
    runRef.current += 1
    // The abandoned run may still be in flight, but it no longer blocks the next
    // one: its result is discarded by the run check above.
    inFlightRef.current = false
    setStatus('idle')
  }, [])

  return { status, compose, reset }
}
